import React, { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatsGrid, StatItem, PaymentList, Payment, PaymentAllocation, StatusActionButton, BottomSheet, ExecutorSelector } from '../../../shared/ui';
import { paymentsV2Api, PaymentV2, PaymentV2Filters, CreatePaymentV2Request } from '../../../api/paymentsV2Api';
import { paymentAllocationV2Api, PaymentAllocationDetailsV2 } from '../../../api/paymentAllocationV2Api';
import { sitePaymentAllocationV2Api, PaymentAllocationDetailsV2 as SitePaymentAllocationDetailsV2 } from '../../../api/sitePaymentAllocationV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useDashboardCounters } from '../../../shared/contexts/DashboardCountersContext';
import PaymentsV2Filter, { PaymentsV2FilterState } from '../components/PaymentsV2Filter';
import PaymentAllocationBottomSheet from '../components/PaymentAllocationBottomSheet';
import './PaymentsV2Card.css';

interface PaymentsV2CardProps {
  siteId: number;
}

export interface PaymentsV2CardHandle {
  openFullscreen: () => void;
}

const PaymentsV2Card = forwardRef<PaymentsV2CardHandle, PaymentsV2CardProps>(({ siteId }, ref) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Ленивая загрузка: по умолчанию свернута
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Отслеживаем открытие fullscreen
  const [payments, setPayments] = useState<PaymentV2[]>([]);
  const [statistics, setStatistics] = useState<{ 
    total: number; 
    thisMonth: number;
    totalAmount: number;
  } | null>(null);
  const [loading, setLoading] = useState(false); // Начинаем с false, т.к. не загружаем сразу
  const [filters, setFilters] = useState<PaymentsV2FilterState>({});
  const [paymentAllocations, setPaymentAllocations] = useState<Map<number, PaymentAllocationDetailsV2>>(new Map());
  const [loadingAllocations, setLoadingAllocations] = useState<Record<number, boolean>>({});
  const [hasFinanceViewPermission, setHasFinanceViewPermission] = useState<boolean>(true);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [allocationSheetOpen, setAllocationSheetOpen] = useState(false);
  const [selectedPaymentForAllocation, setSelectedPaymentForAllocation] = useState<Payment | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newPayment, setNewPayment] = useState<CreatePaymentV2Request>({
    siteExecutorId: undefined,
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    comment: ''
  });
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const countersContext = useDashboardCounters();
  
  // Получаем счетчик из Context (если доступен) или null
  const paymentCounter = countersContext?.counters?.unallocatedPayments.hasAccess 
    ? { 
        count: countersContext.counters.unallocatedPayments.count, 
        amount: countersContext.counters.unallocatedPayments.totalAmount 
      }
    : null;
  const countersLoading = countersContext?.loading ?? false;

  useImperativeHandle(ref, () => ({
    openFullscreen() {
      setIsCollapsed(false);
      setIsFullscreenOpen(true);
    },
  }));

  // Загрузка данных
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Подготавливаем фильтры для API
      // При отсутствии периода — ограничиваем 30 платежами (как в сменах)
      const apiFilters: PaymentV2Filters = {};
      
      if (filters.startDate) {
        const adjustedStartDate = new Date(filters.startDate);
        adjustedStartDate.setHours(0, 0, 0, 0);
        apiFilters.startDate = adjustedStartDate;
      }
      
      if (filters.endDate) {
        const adjustedEndDate = new Date(filters.endDate);
        adjustedEndDate.setHours(23, 59, 59, 999);
        apiFilters.endDate = adjustedEndDate;
      }
      
      if (filters.userId) {
        apiFilters.userId = filters.userId;
      }
      
      if (!filters.startDate && !filters.endDate) {
        apiFilters.limit = 30;
      }
      
      let paymentsData: PaymentV2[] = [];
      let hasPermission = true;
      
      try {
        // Пытаемся получить платежи через основной метод (требует права FINANCE_VIEW_DATA)
        paymentsData = await paymentsV2Api.getSitePayments(siteId, apiFilters);
        
        // Если запрос прошел успешно (даже с пустым списком), значит права есть
        // Используем полученные данные и не трогаем личный API
        hasPermission = true;
      } catch (error: any) {
        // Если получили ошибку 403 (Forbidden), значит нет прав на просмотр финансов
        // В этом случае получаем только личные платежи через отдельный API
        if (error?.response?.status === 403) {
          hasPermission = false;
          try {
            paymentsData = await paymentsV2Api.getMySitePayments(siteId, {
              startDate: apiFilters.startDate,
              endDate: apiFilters.endDate,
              limit: apiFilters.limit
            });
          } catch (myPaymentsError) {
            console.error('Error loading my site payments:', myPaymentsError);
            paymentsData = [];
          }
        } else {
          // Для других ошибок просто логируем
          console.error('Error loading site payments:', error);
          paymentsData = [];
          hasPermission = true; // Предполагаем, что права есть, но произошла другая ошибка
        }
      }
      
      setHasFinanceViewPermission(hasPermission);
      
      setPayments(paymentsData);
      
      // Оптимизированная загрузка распределений для всех платежей сразу
      if (paymentsData.length > 0 && hasPermission) {
        try {
          const paymentIds = paymentsData.map(p => p.id);
          const siteAllocationsMap = await sitePaymentAllocationV2Api.getBatchPaymentAllocations(siteId, paymentIds);
          
          // Преобразуем SitePaymentAllocationDetailsV2 в PaymentAllocationDetailsV2
          const allocationsMap = new Map<number, PaymentAllocationDetailsV2>();
          siteAllocationsMap.forEach((siteAllocation, paymentId) => {
            allocationsMap.set(paymentId, {
              payment: siteAllocation.payment,
              paymentLines: siteAllocation.paymentLines,
              activityAllocations: siteAllocation.activityAllocations ?? siteAllocation.sessionAllocations ?? [],
              contractAllocations: siteAllocation.contractAllocations || [],
              totalAllocatedAmount: siteAllocation.totalAllocatedAmount,
              remainingAmount: siteAllocation.remainingAmount,
              totalAllocations: siteAllocation.totalAllocations
            });
          });
          
          setPaymentAllocations(allocationsMap);
        } catch (error) {
          console.error('Error loading payment allocations:', error);
          // Не блокируем загрузку, если распределения не загрузились
        }
      } else {
        // Очищаем распределения если нет платежей или нет прав
        setPaymentAllocations(new Map());
      }
      
      // Вычисляем статистику
      const now = new Date();
      const stats = {
        total: paymentsData.length,
        thisMonth: paymentsData.filter((p: PaymentV2) => {
          const paymentDate = new Date(p.date);
          return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
        }).length,
        totalAmount: paymentsData.reduce((sum: number, p: PaymentV2) => sum + (p.amount || 0), 0)
      };
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId, user?.id, filters]);

  // Загрузка прав доступа
  const loadPermissions = useCallback(async () => {
    try {
      const hasPermission = await paymentsV2Api.checkEditPermission(siteId);
      setCanEdit(hasPermission);
    } catch (error) {
      console.error('Ошибка при проверке прав:', error);
      setCanEdit(false);
    }
  }, [siteId]);

  // Счетчики загружаются через Context в ObjectPage

  // При закрытии карточки очищаем данные, чтобы при следующем открытии не показывать старые
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = !isCollapsed || isFullscreenOpen;
    if (wasOpenRef.current && !isOpen) {
      setPayments([]);
      setStatistics(null);
      setPaymentAllocations(new Map());
      setError(null);
      setLoading(false);
    }
    wasOpenRef.current = isOpen;
  }, [isCollapsed, isFullscreenOpen]);

  // Ленивая загрузка: загружаем данные когда карточка раскрыта (inline) или fullscreen модальное окно открыто
  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      // Загружаем данные при открытии карточки или при изменении фильтров
      loadData();
      loadPermissions();
    }
  }, [isCollapsed, isFullscreenOpen, loadData, loadPermissions]);

  // Очищаем фильтр по пользователю, если нет прав на просмотр финансов
  useEffect(() => {
    if (!hasFinanceViewPermission && filters.userId) {
      setFilters(prev => ({ ...prev, userId: undefined }));
    }
  }, [hasFinanceViewPermission, filters.userId]);

  // Форматирование суммы для статистики
  const formatAmountForStats = (amount: number): string => {
    return `${amount.toLocaleString('ru-RU')} ₽`;
  };

  // Подготовка данных для StatsGrid
  const statsData: StatItem[] = [
    {
      id: 'total',
      value: statistics?.total || 0,
      label: 'Всего платежей',
      icon: 'ri-money-dollar-circle-line',
      color: 'muted'
    },
    {
      id: 'this-month',
      value: statistics?.thisMonth || 0,
      label: 'В этом месяце',
      icon: 'ri-calendar-line',
      color: 'success'
    },
    {
      id: 'total-amount',
      value: formatAmountForStats(statistics?.totalAmount || 0),
      label: 'Общая сумма',
      icon: 'ri-wallet-line',
      color: 'info'
    }
  ];

  // Форматирование суммы
  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU')} ₽`;
  };

  // Форматирование времени
  const formatTime = useCallback((timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Форматирование даты
  const formatDate = useCallback((timeString: string) => {
    const date = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }, []);

  // Обработчик раскрытия платежа (распределения уже загружены при загрузке данных)
  const handlePaymentExpand = useCallback((payment: Payment) => {
    // Распределения уже загружены при загрузке платежей, ничего не делаем
    // Оставляем для совместимости с PaymentList
  }, []);

  // Обработчик распределения платежа
  const handleAllocatePayment = useCallback((payment: Payment) => {
    const paymentV2 = payments.find(p => p.id === payment.id);
    if (!paymentV2) return;

    // Рассчитываем уже распределенную сумму
    const allocationsData = paymentAllocations.get(payment.id);
    const allocatedAmount = allocationsData?.totalAllocatedAmount || 0;
    const remainingAmount = paymentV2.amount - allocatedAmount;

    setSelectedPaymentForAllocation({
      ...payment,
      amount: paymentV2.amount,
      allocatedAmount,
      remainingAmount,
      siteId
    } as any);
    setAllocationSheetOpen(true);
  }, [payments, paymentAllocations, siteId]);

  // Обработчик успешного распределения
  const handleAllocationSuccess = useCallback(() => {
    // Перезагружаем данные
    loadData();
  }, [loadData]);

  // Обработчик создания выплаты
  const handleCreatePayment = useCallback(async () => {
    if ((!newPayment.siteExecutorId && !newPayment.userId) || newPayment.amount <= 0 || !newPayment.date) {
      setError('Заполните все обязательные поля');
      return;
    }
    
    setIsCreating(true);
    setError(null);
    try {
      // Отправляем comment как description
      const paymentRequest: CreatePaymentV2Request = {
        ...newPayment,
        description: newPayment.comment || ''
      };
      
      await paymentsV2Api.createPayment(siteId, paymentRequest);
      console.log('Выплата успешно создана');
      
      // После создания обновляем список
      await loadData();
      
      // Сбрасываем форму и закрываем
      setNewPayment({
        siteExecutorId: undefined,
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        comment: ''
      });
      setShowCreateSheet(false);
    } catch (error: any) {
      console.error('Ошибка при создании выплаты:', error);
      setError(error?.response?.data || 'Ошибка при создании выплаты');
    } finally {
      setIsCreating(false);
    }
  }, [newPayment, siteId, loadData]);

  // Обработчик изменения полей формы
  const handleInputChange = useCallback((field: keyof CreatePaymentV2Request, value: string | number) => {
    setNewPayment(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null); // Сбрасываем ошибку при изменении
  }, []);

  // Обработчик закрытия формы
  const handleCloseCreateSheet = useCallback(() => {
    setShowCreateSheet(false);
    setNewPayment({
      siteExecutorId: undefined,
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      comment: ''
    });
    setError(null);
  }, []);

  // Преобразование PaymentV2 в Payment для PaymentList
  const paymentListData: Payment[] = useMemo(() => {
    return payments.map((paymentV2): Payment => {
      const allocationsData = paymentAllocations.get(paymentV2.id);
      
      // Создаем иерархическую структуру: PaymentLine -> childAllocations
      const allocations: PaymentAllocation[] = [];
      
      // Группируем активности по paymentLineId и по смене (sessionId или дата+объект)
      // Ключ смены: sessionId если есть, иначе "date_site" для группировки по дню и объекту
      const activitiesByLineAndSession = new Map<number, Map<string | number, PaymentAllocation[]>>();
      const contractsByLineId = new Map<number, PaymentAllocation[]>();
      
      // Активности: группируем по статье и по смене (sessionId с бэкенда или дата+объект)
      if (allocationsData?.activityAllocations) {
        allocationsData.activityAllocations.forEach(allocation => {
          const activityNode: PaymentAllocation = {
            allocationId: allocation.allocationId,
            allocationType: 'activity',
            sessionTimeStart: allocation.sessionTimeStart,
            sessionTimeEnd: allocation.sessionTimeEnd,
            sessionHours: allocation.sessionHours,
            allocationAmount: allocation.allocationAmount,
            sessionSiteName: allocation.sessionSiteName,
            sessionActivityTypeOfWorkName: allocation.sessionActivityTypeOfWorkName
          };
          const lineId = allocation.paymentLineId;
          const sessionKey: string | number = allocation.sessionId != null && allocation.sessionId > 0
            ? allocation.sessionId
            : `${allocation.sessionTimeStart?.slice(0, 10) ?? ''}_${allocation.sessionSiteName ?? ''}`;
          if (!activitiesByLineAndSession.has(lineId)) {
            activitiesByLineAndSession.set(lineId, new Map());
          }
          const bySession = activitiesByLineAndSession.get(lineId)!;
          if (!bySession.has(sessionKey)) {
            bySession.set(sessionKey, []);
          }
          bySession.get(sessionKey)!.push(activityNode);
        });
      }
      
      // Контракты (без группировки по сменам)
      if (allocationsData?.contractAllocations) {
        allocationsData.contractAllocations.forEach(allocation => {
          const childAllocation: PaymentAllocation = {
            allocationId: allocation.allocationId,
            allocationType: 'contract',
            allocationAmount: allocation.allocationAmount,
            contractTitle: allocation.contractTitle,
            contractDescription: allocation.contractDescription
          };
          const lineId = allocation.paymentLineId;
          if (!contractsByLineId.has(lineId)) {
            contractsByLineId.set(lineId, []);
          }
          contractsByLineId.get(lineId)!.push(childAllocation);
        });
      }
      
      // Строим дерево: PaymentLine -> [ Session1, Session2, ..., Contract1, ... ]; каждая Session -> [ Activity, ... ]
      if (allocationsData?.paymentLines) {
        allocationsData.paymentLines.forEach(line => {
          const bySession = activitiesByLineAndSession.get(line.id);
          const sessionNodes: PaymentAllocation[] = [];
          if (bySession) {
            const entries = Array.from(bySession.entries());
            entries.sort(([, a], [, b]) => {
              const tA = a[0]?.sessionTimeStart;
              const tB = b[0]?.sessionTimeStart;
              if (!tA) return 1;
              if (!tB) return -1;
              return new Date(tA).getTime() - new Date(tB).getTime();
            });
            entries.forEach(([sessionKey, activityNodes], index) => {
              const sumAmount = activityNodes.reduce((s, a) => s + a.allocationAmount, 0);
              const first = activityNodes[0];
              const sessionIdNum = typeof sessionKey === 'number' ? sessionKey : -line.id * 10000 - index;
              sessionNodes.push({
                allocationId: sessionIdNum,
                allocationType: 'session',
                sessionTimeStart: first?.sessionTimeStart,
                sessionTimeEnd: activityNodes.length ? activityNodes[activityNodes.length - 1]?.sessionTimeEnd : undefined,
                sessionSiteName: first?.sessionSiteName,
                allocationAmount: sumAmount,
                childAllocations: activityNodes
              });
            });
          }
          const contractNodes = contractsByLineId.get(line.id) || [];
          const childAllocations = [...sessionNodes, ...contractNodes];
          
          allocations.push({
            allocationId: line.id,
            allocationType: 'paymentLine',
            paymentLineType: line.type,
            paymentLineDescription: line.description,
            allocationAmount: line.amount,
            isAllocated: line.isAllocated,
            childAllocations: childAllocations.length > 0 ? childAllocations : undefined
          });
        });
      }

      return {
        id: paymentV2.id,
        userId: paymentV2.userId,
        siteExecutorId: paymentV2.siteExecutorId,
        userName: paymentV2.user?.name,
        date: paymentV2.date,
        amount: allocationsData?.payment?.amount ?? paymentV2.amount,
        description: paymentV2.description,
        allocatedAmount: allocationsData?.payment?.allocatedAmount,
        remainingAmount: allocationsData?.payment?.remainingAmount,
        signatureAdmin: paymentV2.signatureAdmin,
        signatureUser: paymentV2.signatureUser,
        allocations: allocations.length > 0 ? allocations : undefined
      };
    });
  }, [payments, paymentAllocations]);

  // Форматируем счетчик для отображения
  const formatCounter = useMemo(() => {
    if (!paymentCounter) return undefined;
    const formattedAmount = paymentCounter.amount.toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return [`${paymentCounter.count} / ${formattedAmount} ₽`];
  }, [paymentCounter]);

  return (
    <DashboardCard
      id="payments-v2-card"
      title="Выплаты"
      icon="ri-money-dollar-circle-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      isFullscreenOpen={isFullscreenOpen}
      onFullscreenOpenChange={setIsFullscreenOpen}
      counters={formatCounter}
      countersLoading={countersLoading}
      headerActions={
        <button 
          className="card-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            loadData();
          }}
          disabled={loading}
          title="Обновить данные"
        >
          <i className={loading ? "ri-loader-4-line ri-spin" : "ri-refresh-line"}></i>
        </button>
      }
    >
      {/* Кнопка создания выплаты (только для пользователей с правом FINANCE_EDIT) */}
      {canEdit && (
        <div className="payments-v2-actions" style={{ marginBottom: '16px' }}>
          <StatusActionButton
            variant="primary"
            fullWidth
            icon="ri-add-line"
            onClick={() => setShowCreateSheet(true)}
          >
            Создать выплату
          </StatusActionButton>
        </div>
      )}

      {/* Статистика */}
      <StatsGrid items={statsData} />

      {/* Фильтры по периоду и сотруднику */}
      <PaymentsV2Filter
        filters={filters}
        onFiltersChange={setFilters}
        siteId={siteId}
        className="payments-v2-filter"
        hasFinanceViewPermission={hasFinanceViewPermission}
      />

      {/* Список платежей */}
      <PaymentList
        payments={paymentListData}
        onPaymentExpand={handlePaymentExpand}
        onAllocateClick={handleAllocatePayment}
        showAllocateButton={hasFinanceViewPermission}
        formatTime={formatTime}
        formatDate={formatDate}
        formatAmount={formatAmount}
        loading={loading}
        loadingAllocations={loadingAllocations}
        emptyState={{
          icon: 'ri-money-dollar-circle-line',
          title: 'Платежей пока нет',
          subtitle: 'Выплаты будут отображаться здесь'
        }}
      />

      {/* BottomSheet для распределения платежа */}
      <PaymentAllocationBottomSheet
        isOpen={allocationSheetOpen}
        onClose={() => setAllocationSheetOpen(false)}
        payment={selectedPaymentForAllocation}
        siteId={siteId}
        onSuccess={handleAllocationSuccess}
      />

      {/* BottomSheet для создания выплаты */}
      <BottomSheet
        isOpen={showCreateSheet}
        onClose={handleCloseCreateSheet}
        title="Создать выплату"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <form 
          className="create-payment-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleCreatePayment();
          }}
        >
          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Выбор сотрудника */}
          <ExecutorSelector
            value={newPayment.siteExecutorId ?? null}
            onChange={(siteExecutorId: number | null) => setNewPayment(prev => ({
              ...prev,
              siteExecutorId: siteExecutorId ?? undefined,
              userId: undefined
            }))}
            siteId={siteId}
            label="Исполнитель"
            placeholder="Выберите сотрудника"
            required
          />
          
          {/* Сумма */}
          <div className="form-group">
            <label htmlFor="payment-amount">Сумма (₽) *</label>
            <input
              id="payment-amount"
              type="number"
              value={newPayment.amount || ''}
              onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
              placeholder="Введите сумму выплаты"
              className="form-input"
              min="0"
              step="0.01"
              required
            />
          </div>
          
          {/* Дата выплаты */}
          <div className="form-group">
            <label htmlFor="payment-date">Дата выплаты *</label>
            <input
              id="payment-date"
              type="date"
              value={newPayment.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="form-input"
              required
            />
          </div>
          
          {/* Комментарий */}
          <div className="form-group">
            <label htmlFor="payment-comment">Комментарий</label>
            <textarea
              id="payment-comment"
              value={newPayment.comment || ''}
              onChange={(e) => handleInputChange('comment', e.target.value)}
              placeholder="Комментарий к выплате (необязательно)"
              className="form-textarea"
              rows={3}
            />
          </div>
          
          {/* Кнопки действий */}
          <div className="form-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleCloseCreateSheet}
              disabled={isCreating}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="submit"
              variant="primary"
              disabled={(!newPayment.siteExecutorId && !newPayment.userId) || newPayment.amount <= 0 || 
                       !newPayment.date || isCreating}
              loading={isCreating}
              icon="ri-add-line"
            >
              Создать
            </StatusActionButton>
          </div>
        </form>
      </BottomSheet>
    </DashboardCard>
  );
});

PaymentsV2Card.displayName = 'PaymentsV2Card';

export default PaymentsV2Card;

